import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import { Combobox } from '@/shared/components/ui/combobox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { formatMoney, parseMoney } from '@/shared/lib/money';
import { MoneyInput } from '@/features/ventas/components/MoneyInput';
import { listActiveCuentasContables } from '@/shared/api/controllers/cuentasContables';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { getCajaSaldo, registrarCargosMp, type MovimientoRow } from '../api';
import { CATEGORIAS_SIN_CUENTA_CONTABLE } from './ReclasificarCuentaDialog';

type CuentaOpcion = { key: string; cuenta: string; categoria: string };

/**
 * Columnas de la lista (encabezado y filas): #, cuenta, importe, quitar. En el
 * celular el importe baja a un segundo renglón, debajo de la cuenta: en una
 * sola fila la cuenta quedaba en "G.".
 */
const COLS =
  'grid grid-cols-[1.25rem_1fr_2rem] gap-2 px-3 sm:grid-cols-[1.5rem_1fr_10rem_2.25rem] sm:gap-3';
/**
 * `cuenta: undefined` = todavía no se tocó → toma la cuenta por defecto (la
 * que el botón viejo imputaba sola); sólo la PRIMERA línea arranca así, las
 * que se agregan van vacías porque son otros cargos (IVA, retenciones…).
 * `null` = el usuario la borró a propósito.
 */
type Cargo = { id: number; cuenta?: CuentaOpcion | null; monto: string };

/** Lo que el botón viejo cargaba siempre: costo financiero de MercadoPago. */
const CUENTA_DEFECTO_KEY = 'MP - Costo Financiero||Gastos y Comis. Bancarias';

/**
 * Botón amarillo de Tesorería: cargos que MercadoPago descontó de un cobro
 * (suelen ser 4–5) o de un pago (1–2). Se agregan uno a uno en el mismo modal
 * — Cuenta Contable (con su Categoría) + Importe — y al confirmar cada uno
 * queda como un Egreso propio, hijo del movimiento (ver
 * `POST /api/tesoreria/movimiento/:id/cargos-mp`). Todos o ninguno.
 *
 * La cuenta y la categoría se eligen JUNTAS (mismo selector que Reimputar):
 * elegirlas por separado permitiría armar un par que no existe en el plan de
 * cuentas, y el server lo rechazaría.
 */
export function CargosMercadoPagoDialog({
  caja,
  moneda,
  parent,
  onClose,
}: {
  caja: string;
  moneda: 'ARS' | 'MXN' | 'USD';
  /** El cobro (Ingreso) o pago (Egreso) de MercadoPago. */
  parent: MovimientoRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  // Nova MP: los cargos salen de Caja Diferencia (neteo), no de la caja MP.
  const isNovaMp = caja === 'Nova Mercado Pago';
  const fundingCaja = isNovaMp ? 'Caja Diferencia' : caja;
  // Sin tipo de cambio: el botón sólo aparece en MercadoPago / Nova Mercado
  // Pago, que nunca son cajas en dólares (`inferCajaCurrency`).

  const [cargos, setCargos] = useState<Cargo[]>([{ id: 1, monto: '' }]);
  const [observaciones, setObservaciones] = useState('');

  const cuentasQ = useQuery({
    queryKey: ['cuentas-contables', activeCountry.code, 'activas'],
    queryFn: listActiveCuentasContables,
    staleTime: 5 * 60_000,
  });
  // Mismo criterio que el diálogo de cargar egresos: sin caché, así no se ve
  // un saldo viejo mientras se recalcula.
  const saldoQ = useQuery({
    queryKey: ['tesoreria', 'saldo', fundingCaja],
    queryFn: () => getCajaSaldo(fundingCaja),
    gcTime: 0,
    refetchOnMount: 'always',
  });
  const saldoLoading = saldoQ.isPending || saldoQ.isFetching;
  const saldoDisponible = saldoQ.data?.saldo ?? 0;

  const opciones = useMemo<CuentaOpcion[]>(() => {
    const out: CuentaOpcion[] = [];
    for (const c of cuentasQ.data ?? []) {
      const cuenta = (c.fields.CuentaContable_CC ?? '').trim();
      const categoria = (c.fields.Categoria_CC ?? '').trim();
      // Sin categoría no hay par; las de automatismo las rechaza el server.
      if (!cuenta || !categoria || CATEGORIAS_SIN_CUENTA_CONTABLE.has(categoria)) continue;
      out.push({ key: `${categoria}||${cuenta}`, cuenta, categoria });
    }
    return out.sort(
      (a, b) =>
        a.cuenta.localeCompare(b.cuenta, 'es') || a.categoria.localeCompare(b.categoria, 'es'),
    );
  }, [cuentasQ.data]);
  // En MX el par no existe: las líneas arrancan vacías.
  const cuentaDefecto = opciones.find((o) => o.key === CUENTA_DEFECTO_KEY) ?? null;
  const cuentaDe = (c: Cargo): CuentaOpcion | null =>
    c.cuenta === undefined ? cuentaDefecto : c.cuenta;

  const total = cargos.reduce((s, c) => s + (parseMoney(c.monto) ?? 0), 0);
  const excedido = !saldoLoading && total > saldoDisponible + 0.01;

  const validationError = useMemo<string | null>(() => {
    // Sin catálogo todavía no hay nada para elegir: no es culpa del usuario.
    if (cuentasQ.isPending) return 'Cargando el plan de cuentas…';
    if (saldoLoading) return 'Calculando el saldo disponible…';
    const i = cargos.findIndex((c) => !cuentaDe(c) || !((parseMoney(c.monto) ?? 0) > 0));
    if (i >= 0) {
      return cuentaDe(cargos[i])
        ? `Cargo ${i + 1}: ingresá un importe mayor a cero.`
        : `Cargo ${i + 1}: elegí la cuenta contable.`;
    }
    if (excedido) {
      return isNovaMp
        ? `Los cargos exceden el saldo de Caja Diferencia (${formatMoney(saldoDisponible, moneda)}).`
        : `Los cargos exceden el saldo disponible (${formatMoney(saldoDisponible, moneda)}).`;
    }
    return null;
  }, [
    cargos,
    cuentaDefecto,
    cuentasQ.isPending,
    saldoLoading,
    excedido,
    saldoDisponible,
    isNovaMp,
    moneda,
  ]);

  const mutation = useMutation({
    mutationFn: () =>
      registrarCargosMp(parent.id, {
        caja,
        cargos: cargos.map((c) => ({
          categoria: cuentaDe(c)!.categoria,
          cuentaContable: cuentaDe(c)!.cuenta,
          monto: parseMoney(c.monto) ?? 0,
        })),
        observaciones: observaciones.trim() || undefined,
      }),
    onSuccess: (r) => {
      if (r.outcome === 'created') {
        toast.success(
          `${r.cargos === 1 ? '1 cargo registrado' : `${r.cargos} cargos registrados`} · ${formatMoney(r.total, moneda)}`,
          r.neteoCajaDiferencia ? { description: 'Descontados de Caja Diferencia.' } : undefined,
        );
        qc.invalidateQueries({ queryKey: ['tesoreria'] });
        onClose();
      } else {
        // El server vio otro saldo que el del panel (o el par ya no es válido):
        // se refresca para que los dos números coincidan.
        void saldoQ.refetch();
        toast.warning('No se pudieron registrar los cargos', { description: r.message });
      }
    },
    onError: (err) =>
      toast.error('No se pudieron registrar los cargos', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });

  const setCargo = (id: number, patch: Partial<Cargo>) =>
    setCargos((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const agregar = () =>
    setCargos((cs) => [...cs, { id: Math.max(...cs.map((c) => c.id)) + 1, cuenta: null, monto: '' }]);
  const quitar = (id: number) => setCargos((cs) => cs.filter((c) => c.id !== id));

  const canSubmit = validationError === null && !mutation.isPending;

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent
        className="max-w-3xl"
        processing={mutation.isPending && 'Registrando cargos…'}
      >
        <DialogHeader>
          <DialogTitle>Cargos MercadoPago</DialogTitle>
          <DialogDescription>
            Cargá uno a uno los cargos que descontó MercadoPago. Cada uno queda como
            un egreso vinculado al {(parent.fields.TipoMovimiento_MC ?? '') === 'Egreso' ? 'pago' : 'cobro'}.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <MovimientoVinculado parent={parent} moneda={moneda} />
          {isNovaMp && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Estos cargos se descuentan de <strong>Caja Diferencia</strong>, no de
              MercadoPago: cada uno se registra con un neteo que deja la caja
              MercadoPago en 0.
            </div>
          )}

          {/* Lista de cargos: una sola tarjeta con encabezado, una fila por cargo
              y el pie con "Agregar cargo" + total. Mismas columnas en las tres
              partes para que todo quede alineado. */}
          <div className="overflow-hidden rounded-xl border border-border">
            <div className={`${COLS} border-b border-border bg-muted/50 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground`}>
              <span className="text-center">#</span>
              <span>Cuenta contable</span>
              <span className="hidden text-right sm:block">Importe</span>
              <span className="hidden sm:block" />
            </div>
            <ul className="divide-y divide-border">
              {cargos.map((c, i) => (
                <li key={c.id} className={`${COLS} items-start py-2.5`}>
                  <span className="flex h-10 items-center justify-center text-sm tabular-nums text-muted-foreground">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <Combobox
                      value={cuentaDe(c)}
                      onChange={(v) => setCargo(c.id, { cuenta: v })}
                      items={opciones}
                      getKey={(o) => o.key}
                      getLabel={(o) => o.cuenta}
                      getSublabel={(o) => o.categoria}
                      matcher={(o, q) =>
                        o.cuenta.toLowerCase().includes(q) || o.categoria.toLowerCase().includes(q)
                      }
                      placeholder={cuentasQ.isPending ? 'Cargando…' : 'Buscar cuenta o categoría'}
                      disabled={mutation.isPending || cuentasQ.isPending}
                    />
                    {/* El Combobox cerrado sólo muestra la cuenta, que se repite
                        entre categorías: confirmamos el par elegido. */}
                    {cuentaDe(c) && (
                      <p className="mt-1 truncate px-1 text-xs text-muted-foreground">
                        Categoría: {cuentaDe(c)!.categoria}
                      </p>
                    )}
                  </div>
                  {/* Misma altura que el Combobox (h-10): el `filled` de Input es h-12. */}
                  <MoneyInput
                    variant="filled"
                    className="col-start-2 row-start-2 h-10 px-3 text-right text-sm sm:col-start-auto sm:row-start-auto"
                    placeholder="0,00"
                    aria-label={`Importe del cargo ${i + 1}`}
                    value={c.monto}
                    moneda={moneda}
                    onChange={(v) => setCargo(c.id, { monto: v })}
                  />
                  <button
                    type="button"
                    onClick={() => quitar(c.id)}
                    disabled={cargos.length === 1 || mutation.isPending}
                    aria-label={`Quitar cargo ${i + 1}`}
                    title="Quitar cargo"
                    className="col-start-3 row-start-1 inline-flex h-10 w-full items-center justify-center rounded-md text-muted-foreground sm:col-start-auto sm:row-start-auto transition-colors hover:bg-rose-50 hover:text-rose-600 disabled:pointer-events-none disabled:opacity-30"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex items-start justify-between gap-3 border-t border-border bg-muted/30 px-3 py-2.5">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={agregar}
                disabled={mutation.isPending || cargos.length >= 20}
                title={cargos.length >= 20 ? 'Máximo 20 cargos por cobro/pago.' : undefined}
                className="-ml-1 gap-1.5 font-medium"
              >
                <Plus className="h-4 w-4" aria-hidden />
                Agregar cargo
              </Button>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">
                  Total{' '}
                  <strong className="text-base tabular-nums text-foreground">
                    {formatMoney(total, moneda)}
                  </strong>
                </p>
                <p
                  className={`mt-0.5 flex items-center justify-end gap-1 text-xs ${
                    excedido ? 'font-medium text-rose-700' : 'text-muted-foreground'
                  }`}
                >
                  {saldoLoading ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                      Calculando saldo…
                    </>
                  ) : (
                    <>
                      Disponible{isNovaMp ? ' en Caja Diferencia' : ''}:{' '}
                      {formatMoney(saldoDisponible, moneda)}
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cargos-obs">Observación</Label>
            <Textarea
              id="cargos-obs"
              variant="filled"
              placeholder="Opcional — se guarda en todos los cargos"
              rows={2}
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
            />
          </div>
        </DialogBody>
        <DialogFooter className="sm:items-center">
          {validationError && (
            <p className="mr-auto text-xs font-medium text-rose-700 sm:max-w-sm">
              {validationError}
            </p>
          )}
          <Button type="button" variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
            title={validationError ?? undefined}
            className="gap-2 bg-foreground text-background hover:bg-foreground/90"
          >
            {cargos.length === 1 ? 'Registrar cargo' : `Registrar ${cargos.length} cargos`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** A qué cobro / pago se le cuelgan los cargos. */
function MovimientoVinculado({
  parent,
  moneda,
}: {
  parent: MovimientoRow;
  moneda: 'ARS' | 'MXN' | 'USD';
}) {
  const f = parent.fields;
  const monto = parseMoney(f.MontoNeto_MC) ?? 0;
  const operacion = (f.IDOperacion_MC ?? '').trim();
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-800">
        {(f.TipoMovimiento_MC ?? '') === 'Egreso' ? 'Pago vinculado' : 'Cobro vinculado'}
      </p>
      <p className="mt-0.5 font-medium text-foreground">
        {formatMoney(monto, moneda)} · {f.Fecha_MC || '—'}
        {f.Hora_MC ? ` · ${f.Hora_MC}` : ''}
      </p>
      <p className="text-xs text-muted-foreground">
        {f.Descripcion_MC || f.CatDesc_MC || 'MercadoPago'}
        {operacion && operacion !== '-' ? ` · Op. ${operacion}` : ''}
      </p>
    </div>
  );
}
